import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { TypedMap } from 'flow/types/TypedMap';
import useContact from 'flow/hooks/useContact';
import useCustomer from 'flow/hooks/useCustomer';
import { ContactFull } from 'flow/types/Contact';
import {
    getCoListeningRemotePartyNumber,
    selectActiveCallOfContact,
} from 'flow/selectors/call-selectors';
import { getCoListeningContact } from 'flow/selectors/contact-selectors';
import { lookupNumber } from 'flow/actions/contact-actions';
import { generatePath } from 'flow/utils/router-utils';
import { routes } from 'flow/types/routes';
import { getExtensionFromContactKey } from 'flow/selectors/selectors';
import CoListeningSupervisorLarge from './CoListeningSupervisorLarge';
import CoListeningSupervisorSmall from './CoListeningSupervisorSmall';
import { addUserPhoneLogEntry } from 'flow/utils/browser-utils';
import { getCountryCode } from 'flow/utils/extension-utils';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';

interface Props {
    subjectContact: TypedMap<ContactFull>;
    subjectNumber: string;
    startTime: Date;
    onHangup: () => void;
    itemSize: 'large' | 'small';
}

const CoListeningSupervisorWrapper = ({
    subjectContact,
    subjectNumber,
    startTime,
    onHangup,
    itemSize,
}: Props) => {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { ownContact } = useContact();
    const { ownCustomer } = useCustomer();
    const ownCountryCode = getCountryCode(ownContact, ownCustomer);

    const fetchedSubjectContact = useAppSelector((state) =>
        getCoListeningContact(
            state,
            subjectNumber,
            subjectContact,
            ownCountryCode,
        ),
    );

    const remotePartyOfSubjectNumber = useAppSelector((state) =>
        getCoListeningRemotePartyNumber(
            state,
            fetchedSubjectContact?.get('key'),
        ),
    );

    const remotePartyOfSubjectContact = useAppSelector((state) =>
        getCoListeningContact(
            state,
            remotePartyOfSubjectNumber,
            null,
            ownCountryCode,
        ),
    );

    const activeCallOfSubject = useAppSelector((state) =>
        selectActiveCallOfContact(state, fetchedSubjectContact?.get('key')),
    );

    const subjectExtension = useAppSelector((state) =>
        getExtensionFromContactKey(state, fetchedSubjectContact?.get('key')),
    );

    useEffect(() => {
        if (subjectNumber && !fetchedSubjectContact) {
            addUserPhoneLogEntry(`Doing numberlookup on ${subjectNumber}`);
            dispatch(lookupNumber(subjectNumber));
        }
    }, [subjectNumber, lookupNumber, fetchedSubjectContact]);

    useEffect(() => {
        if (remotePartyOfSubjectNumber && !remotePartyOfSubjectContact) {
            addUserPhoneLogEntry(
                `Doing numberlookup on ${remotePartyOfSubjectNumber}`,
            );
            dispatch(lookupNumber(remotePartyOfSubjectNumber));
        }
    }, [remotePartyOfSubjectNumber, lookupNumber, remotePartyOfSubjectContact]);

    const handleChat = () => {
        history.push(
            generatePath(routes['messages.id.redirect'], {
                chatUserKey: subjectExtension?.get('chatUserKey'),
            }),
        );
    };

    const coListeningComponentProps = {
        subjectContact,
        subjectNumber,
        remotePartyOfSubjectContact,
        remotePartyOfSubjectNumber,
        ownCountryCode,
        startTime,
        onHangup,
        activeCallOfSubject,
        handleChat: subjectExtension ? handleChat : null,
    };

    return itemSize === 'large' ? (
        <CoListeningSupervisorLarge {...coListeningComponentProps} />
    ) : (
        <CoListeningSupervisorSmall {...coListeningComponentProps} />
    );
};

export default CoListeningSupervisorWrapper;
